// ManagerPermissions.jsx
// Owner-only component for configuring what managers can access

import React, { useState, useEffect } from 'react';
import api from './axiosConfig';

const ManagerPermissions = () => {
  const [managers, setManagers] = useState([]);
  const [permissionLabels, setPermissionLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // Track which manager is being saved
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadManagers();
  }, []);

  const loadManagers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/permissions/managers');
      setManagers(response.data.managers || []);
      setPermissionLabels(response.data.permission_labels || {});
    } catch (err) {
      console.error('Failed to load managers:', err);
      setError('Failed to load manager permissions');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = async (managerSecureId, permissionKey, currentValue) => {
    // Find the manager
    const manager = managers.find(m => m.secure_id === managerSecureId);
    if (!manager) return;

    // Optimistically update UI
    setManagers(prev => prev.map(m => {
      if (m.secure_id === managerSecureId) {
        return {
          ...m,
          permissions: {
            ...m.permissions,
            [permissionKey]: !currentValue
          }
        };
      }
      return m;
    }));

    // Save to backend
    setSaving(managerSecureId);
    setError(null);
    setSuccess(null);

    try {
      await api.put(`/permissions/managers/${managerSecureId}/permissions`, {
        permissions: {
          [permissionKey]: !currentValue
        }
      });
      setSuccess(`Permissions updated for ${manager.name}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to update permission:', err);
      // Revert on error
      setManagers(prev => prev.map(m => {
        if (m.secure_id === managerSecureId) {
          return {
            ...m,
            permissions: {
              ...m.permissions,
              [permissionKey]: currentValue
            }
          };
        }
        return m;
      }));
      setError('Failed to update permission');
    } finally {
      setSaving(null);
    }
  };

  // Group permissions by category
  const permissionGroups = {
    'Core Access': ['dashboard', 'team', 'time', 'documents'],
    'Team Details': ['team_salaries', 'approve_holidays', 'manage_training'],
    'Financial': ['money', 'reports', 'commission'],
    'Operations': ['stock', 'calculator', 'import_data'],
  };

  const styles = {
    container: {
      padding: '24px',
      maxWidth: '1200px',
      margin: '0 auto',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    header: {
      marginBottom: '32px',
    },
    title: {
      fontSize: '28px',
      fontWeight: '700',
      color: '#1e293b',
      margin: '0 0 8px 0',
    },
    subtitle: {
      color: '#64748b',
      fontSize: '16px',
      margin: 0,
    },
    alert: {
      padding: '12px 16px',
      borderRadius: '8px',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    alertError: {
      background: '#fef2f2',
      border: '1px solid #fecaca',
      color: '#dc2626',
    },
    alertSuccess: {
      background: '#f0fdf4',
      border: '1px solid #bbf7d0',
      color: '#16a34a',
    },
    managerCard: {
      background: 'white',
      borderRadius: '16px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      marginBottom: '24px',
      overflow: 'hidden',
    },
    managerHeader: {
      padding: '20px 24px',
      borderBottom: '1px solid #e2e8f0',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
    },
    avatar: {
      width: '48px',
      height: '48px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontWeight: '600',
      fontSize: '18px',
    },
    managerName: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1e293b',
      margin: 0,
    },
    managerEmail: {
      fontSize: '14px',
      color: '#64748b',
      margin: '4px 0 0 0',
    },
    permissionsGrid: {
      padding: '24px',
    },
    groupTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#64748b',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      margin: '0 0 12px 0',
      paddingTop: '16px',
      borderTop: '1px solid #f1f5f9',
    },
    groupTitleFirst: {
      borderTop: 'none',
      paddingTop: 0,
    },
    permissionRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 0',
    },
    permissionLabel: {
      fontSize: '15px',
      color: '#334155',
    },
    toggle: {
      position: 'relative',
      width: '44px',
      height: '24px',
      cursor: 'pointer',
    },
    toggleTrack: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: '12px',
      transition: 'background-color 0.2s',
    },
    toggleThumb: {
      position: 'absolute',
      top: '2px',
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      background: 'white',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      transition: 'transform 0.2s',
    },
    noManagers: {
      textAlign: 'center',
      padding: '60px 20px',
      color: '#64748b',
    },
    loadingSpinner: {
      textAlign: 'center',
      padding: '60px 20px',
    },
    savingOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(255,255,255,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingSpinner}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e2e8f0',
            borderTopColor: '#0d9488',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px',
          }} />
          <p>Loading managers...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); }}`}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Manager Permissions</h1>
        <p style={styles.subtitle}>
          Control what your managers can access. Changes take effect immediately.
        </p>
      </div>

      {error && (
        <div style={{...styles.alert, ...styles.alertError}}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          {error}
        </div>
      )}

      {success && (
        <div style={{...styles.alert, ...styles.alertSuccess}}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          {success}
        </div>
      )}

      {managers.length === 0 ? (
        <div style={styles.noManagers}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" style={{marginBottom: '16px'}}>
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          <h3 style={{color: '#475569', margin: '0 0 8px 0'}}>No Managers Yet</h3>
          <p>When you add managers to your account, you can control their access here.</p>
        </div>
      ) : (
        managers.map(manager => (
          <div key={manager.secure_id} style={{...styles.managerCard, position: 'relative'}}>
            {saving === manager.secure_id && (
              <div style={styles.savingOverlay}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  border: '2px solid #e2e8f0',
                  borderTopColor: '#0d9488',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }} />
              </div>
            )}
            
            <div style={styles.managerHeader}>
              <div style={styles.avatar}>
                {manager.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
              </div>
              <div>
                <h3 style={styles.managerName}>{manager.name}</h3>
                <p style={styles.managerEmail}>{manager.email}</p>
              </div>
            </div>
            
            <div style={styles.permissionsGrid}>
              {Object.entries(permissionGroups).map(([groupName, permKeys], groupIndex) => (
                <div key={groupName}>
                  <h4 style={{
                    ...styles.groupTitle,
                    ...(groupIndex === 0 ? styles.groupTitleFirst : {})
                  }}>
                    {groupName}
                  </h4>
                  {permKeys.map(permKey => (
                    <div key={permKey} style={styles.permissionRow}>
                      <span style={styles.permissionLabel}>
                        {permissionLabels[permKey] || permKey}
                      </span>
                      <div
                        style={styles.toggle}
                        onClick={() => handlePermissionToggle(
                          manager.secure_id,
                          permKey,
                          manager.permissions[permKey]
                        )}
                      >
                        <div style={{
                          ...styles.toggleTrack,
                          backgroundColor: manager.permissions[permKey] ? '#0d9488' : '#cbd5e1',
                        }} />
                        <div style={{
                          ...styles.toggleThumb,
                          transform: manager.permissions[permKey] ? 'translateX(20px)' : 'translateX(2px)',
                        }} />
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default ManagerPermissions;